import { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
}

interface InlineSelectProps {
  value: string;
  options: SelectOption[];
  onSave: (value: string) => Promise<void>;
  placeholder?: string;
  className?: string;
}

export function InlineSelect({
  value,
  options,
  onSave,
  placeholder = 'Select...',
  className,
}: InlineSelectProps) {
  const [isSaving, setIsSaving] = useState(false);
  const saveCountRef = useRef(0);
  // Local state for optimistic updates
  const [displayValue, setDisplayValue] = useState(value);

  // Sync with prop when it changes (e.g., after successful save)
  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const handleChange = (newValue: string) => {
    if (newValue !== displayValue) {
      const previousValue = displayValue;
      setDisplayValue(newValue); // Optimistic update
      saveCountRef.current++;
      setIsSaving(true);

      onSave(newValue)
        .catch(() => {
          // Only rollback if no subsequent change has happened
          setDisplayValue(prev => prev === newValue ? previousValue : prev);
        })
        .finally(() => {
          saveCountRef.current--;
          if (saveCountRef.current === 0) setIsSaving(false);
        });
    }
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Select
        value={displayValue}
        onValueChange={handleChange}
      >
        <SelectTrigger className="flex-1">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isSaving && (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
      )}
    </div>
  );
}
